import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState, useCallback, useRef } from "react";
import { CustomersApi, TransactionsApi } from "../../../api/common/apiIndex";
import { useIsAdmin } from "../hooks/useIsAdmin";
import { FiPhone } from "react-icons/fi";
import { MdOutlineAttachMoney, MdOutlineSchedule, MdOutlineTrendingUp } from "react-icons/md";
import { toast } from "react-toastify";
import { getAlpha2, getCountryName } from "../../common/commonUtils/countries";
import Loader from "../../common/commonUtils/Loader";
import "./CustomerDetails.css";

const normalizeCountryIso2 = (value) => {
  const code = getAlpha2(value || "");
  return code ? code.toUpperCase() : "";
};

const countryLabel = (value) => {
  const iso = normalizeCountryIso2(value);
  return getCountryName(iso) || iso || "";
};

const fmtMoney = (n, currency) => {
  if (n == null || n === "") return "—";
  const value = Number(n);
  if (Number.isNaN(value)) return "—";
  try {
    return new Intl.NumberFormat("sv-SE", {
      style: currency ? "currency" : undefined,
      currency: currency || undefined,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return new Intl.NumberFormat("sv-SE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
  }
};

const relativeTimeFromNow = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const now = new Date();
  const diff = d - now;
  const abs = Math.abs(diff);
  const rtf = new Intl.RelativeTimeFormat("en", { numeric: "auto" });
  if (abs < 60_000) return rtf.format(Math.round(diff / 1000), "second");
  if (abs < 3_600_000) return rtf.format(Math.round(diff / 60_000), "minute");
  if (abs < 86_400_000) return rtf.format(Math.round(diff / 3_600_000), "hour");
  return rtf.format(Math.round(diff / 86_400_000), "day");
};

const StatusBadge = ({ status }) => {
  if (!status) return null;
  const s = String(status).toUpperCase();
  const cls =
    s === "PAID" ? "bg-success"
      : s === "READY" ? "bg-warning text-dark"
        : s === "PROCESSING" ? "bg-info text-dark"
          : "bg-secondary";
  return <span className={`badge ${cls}`}>{s}</span>;
};

const formatNumber = (value) => {
  if (value == null) return "—";
  const numeric = Number(value);
  try {
    return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Number.isNaN(numeric) ? 0 : numeric);
  } catch {
    return `${numeric}`;
  }
};

const formatDateTime = (iso) => {
  if (!iso) return "—";
  const date = new Date(iso);
  return `${date.toLocaleDateString()} • ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const mergePages = (a, b) => {
  const ac = Array.isArray(a?.content) ? a.content : [];
  const bc = Array.isArray(b?.content) ? b.content : [];
  const content = [...ac, ...bc].sort((x, y) => new Date(y?.createdAt || 0) - new Date(x?.createdAt || 0));
  return {
    content,
    totalElements: (a?.totalElements || ac.length) + (b?.totalElements || bc.length),
    totalPages: 1,
    size: content.length,
    number: 0,
  };
};

export default function CustomerDetails() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const isAdmin = useIsAdmin();

  const [tab, setTab] = useState("all");

  const selfRef = useRef({ id: null, uuid: null });

  const { data: person, isLoading: pLoading, isError: pErr, error: pError } = useQuery({
    queryKey: ["customer", "detail", uuid],
    queryFn: () => CustomersApi.getByUuid(uuid),
    enabled: !!uuid,
  });

  if (person && (selfRef.current.id !== person.id || selfRef.current.uuid !== person.uuid)) {
    selfRef.current = { id: person.id ?? null, uuid: person.uuid ?? null };
  }
  const personId = person?.id;

  const { data: txPage, isLoading: txLoading, isError: txErr, error: txError } = useQuery({
    queryKey: ["transactions_byCustomer", personId, { page: 0, size: 200, sort: "createdAt,desc" }],
    queryFn: async () => {
      const [asSender, asRecipient] = await Promise.all([
        TransactionsApi.listBySenderId(personId, { page: 0, size: 200, sort: "createdAt,desc" }),
        TransactionsApi.listByRecipientId(personId, { page: 0, size: 200, sort: "createdAt,desc" }),
      ]);
      return mergePages(asSender, asRecipient);
    },
    enabled: !!personId,
  });

  const loading = pLoading || (!!personId && txLoading);

  const p = useMemo(() => (person || {}), [person]);
  const fullName = useMemo(() => (`${p?.firstName ?? ""} ${p?.lastName ?? ""}`.trim() || p?.name || "Customer"), [p]);
  const personCountryIso = useMemo(() => normalizeCountryIso2(p.country), [p.country]);
  const personCountryFlag = personCountryIso ? `https://flagcdn.com/48x36/${personCountryIso.toLowerCase()}.png` : null;
  const personCountryLabel = useMemo(() => countryLabel(p.country), [p.country]);

  const initialsFromName = useCallback(
    (name) => (name || "").split(" ").filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join("") || "•",
    []
  );

  const txs = useMemo(() => (txPage?.content ?? []), [txPage]);

  const sentTxs = useMemo(
    () => txs.filter((t) => String(t?.senderId ?? "") === String(personId ?? "")),
    [txs, personId]
  );
  const receivedTxs = useMemo(
    () => txs.filter((t) => String(t?.recipientId ?? "") === String(personId ?? "")),
    [txs, personId]
  );

  const currentTxs = tab === "sent" ? sentTxs : tab === "received" ? receivedTxs : txs;
  const handleCopy = useCallback((text) => {
    if (!text) return;
    navigator.clipboard?.writeText(String(text)).then(() => toast.info("Copied!"));
  }, []);

  const { txCount, totalAmount, unifiedCurrency, lastAt, statusCounts } = useMemo(() => {
    const selfId = selfRef.current.id;
    const currencySet = new Set();
    let total = 0;
    let latest = null;
    const stats = {};
    for (const t of currentTxs) {
      const asSender = String(t?.senderId ?? "") === String(selfId ?? "");
      const amount = asSender ? (t?.sendAmount ?? t?.amount) : (t?.receiveAmount ?? t?.amount);
      const currency = asSender ? (t?.sendCurrency ?? t?.currency) : (t?.receiveCurrency ?? t?.currency);
      total += Number(amount) || 0;
      if (currency) currencySet.add(currency);
      if (t?.createdAt && (!latest || new Date(t.createdAt) > new Date(latest))) latest = t.createdAt;
      const key = String(t?.status || "UNKNOWN").toUpperCase();
      stats[key] = (stats[key] || 0) + 1;
    }
    return {
      txCount: currentTxs.length,
      totalAmount: total,
      unifiedCurrency: currencySet.size === 1 ? [...currencySet][0] : undefined,
      lastAt: latest,
      statusCounts: stats,
    };
  }, [currentTxs]);

  const linkedEntities = useMemo(() => {
    const { id: selfId, uuid: selfUuid } = selfRef.current || {};
    const eq = (a, b) => a != null && b != null && String(a) === String(b);
    const m = new Map();
    txs.forEach((t) => {
      let other = null;
      const isSender = (eq(t?.senderId, selfId) || eq(t?.senderUuid, selfUuid)) && t?.recipientId;
      const isRecipient = (eq(t?.recipientId, selfId) || eq(t?.recipientUuid, selfUuid)) && t?.senderId;
      if (isSender) {
        const iso = normalizeCountryIso2(t.recipientCountry || t.recipientCountryCode);
        other = {
          id: t.recipientId,
          uuid: t.recipientUuid,
          name: t.recipientName,
          phone: t.recipientPhone,
          country: iso,
          countryCode: iso ? iso.toLowerCase() : "",
          countryName: countryLabel(iso),
        };
      } else if (isRecipient) {
        const iso = normalizeCountryIso2(t.senderCountry || t.senderCountryCode);
        other = {
          id: t.senderId,
          uuid: t.senderUuid,
          name: t.senderName,
          phone: t.senderPhone,
          country: iso,
          countryCode: iso ? iso.toLowerCase() : "",
          countryName: countryLabel(iso),
        };
      }
      if (!other?.id) return;
      const key = String(other.id);
      const e = m.get(key) || { ...other, count: 0, lastAt: null, sentCount: 0, receivedCount: 0 };
      if (!e.country && other?.country) {
        e.country = other.country;
        e.countryCode = other.countryCode;
        e.countryName = other.countryName;
      }
      e.count += 1;
      if (isSender) e.sentCount += 1;
      if (isRecipient) e.receivedCount += 1;
      if (t.createdAt && (!e.lastAt || new Date(t.createdAt) > new Date(e.lastAt))) e.lastAt = t.createdAt;
      m.set(key, e);
    });
    return Array.from(m.values()).sort((a, b) => new Date(b.lastAt || 0) - new Date(a.lastAt || 0));
  }, [txs]);

  const allCount = txs.length;
  const sentCount = sentTxs.length;
  const receivedCount = receivedTxs.length;
  const linkedCount = linkedEntities.length;
  const summaryViewLabel = tab === "sent" ? "Sending view" : tab === "received" ? "Receiving view" : "All transfers";
  const formattedLastActivity = formatDateTime(lastAt);
  const lastActivityCaption = lastAt ? `Updated ${relativeTimeFromNow(lastAt)}` : "No recent activity yet";
  const totalCaption = txCount === 0 ? "No transfers yet" : unifiedCurrency ? `In ${unifiedCurrency}` : "Multiple currencies";
  const customerSummaryMetrics = [
    {
      key: "transfers",
      label: "Transfers",
      Icon: MdOutlineTrendingUp,
      accent: "bg-primary-subtle text-primary-emphasis",
      value: formatNumber(txCount),
      caption: summaryViewLabel,
    },
    {
      key: "total-volume",
      label: "Total volume",
      Icon: MdOutlineAttachMoney,
      accent: "bg-success-subtle text-success-emphasis",
      value: fmtMoney(totalAmount, unifiedCurrency),
      caption: totalCaption,
    },
    {
      key: "last-activity",
      label: "Last activity",
      Icon: MdOutlineSchedule,
      accent: "bg-secondary-subtle text-secondary-emphasis",
      value: formattedLastActivity,
      caption: lastActivityCaption,
    },
  ];

  const tabOptions = useMemo(() => {
    const base = [
      {
        key: "all",
        label: "All transfers",
        caption: "Everything in one list",
        icon: "bi-arrow-left-right",
        count: allCount,
      },
      {
        key: "sent",
        label: "Sending",
        caption: "Transfers initiated by this customer",
        icon: "bi-arrow-up-right",
        count: sentCount,
      },
      {
        key: "received",
        label: "Receiving",
        caption: "Transfers received by this customer",
        icon: "bi-arrow-down-left",
        count: receivedCount,
      },
    ];
    if (isAdmin) {
      base.push({
        key: "linked",
        label: "Linked partners",
        caption: "People frequently paired with this customer",
        icon: "bi-people",
        count: linkedCount,
      });
    }
    return base;
  }, [allCount, sentCount, receivedCount, linkedCount, isAdmin]);

  if (loading) {
    return (
      <div
        className="container py-5 d-flex justify-content-center align-items-center"
        style={{ minHeight: "calc(var(--app-viewport-height, 100%) * 0.4)" }}
      >
        <Loader text="Loading…" />
      </div>
    );
  }

  return (
    <div className="container py-3">

      {(pErr || txErr) && (
        <div className="alert alert-danger" role="alert">
          {pErr ? `Failed to load customer. ${pError?.message || ""}` : null}
          {txErr ? ` Failed to load transfers. ${txError?.message || ""}` : null}
        </div>
      )}

      {!(pErr || txErr) && (
        <>
          <div className="card border rounded-4 shadow-sm mb-3">
            <div className="card-body p-3 p-md-4">
              <div className="d-flex flex-wrap align-items-center gap-3">
                <div
                  className=" d-inline-flex align-items-center justify-content-center"
                  style={{ width: 64, height: 64 }}
                >
                  {personCountryFlag ? (
                    <img src={personCountryFlag} alt={personCountryLabel || "Country"} width="48" height="36" className="" />
                  ) : (
                    <span className="fs-4">{initialsFromName(fullName)}</span>
                  )}
                </div>
                <div className="d-flex flex-column">
                  <h2 className="h5 mb-0 text-body-emphasis">{fullName}</h2>
                  <div className="text-body-secondary small">{[personCountryLabel, p.city].filter(Boolean).join(" · ") || "—"}</div>
                  <span className="d-inline-flex align-items-center gap-1 mt-1">
                    <FiPhone />
                    <span>{p.phone}</span>
                    <button type="button" className="btn btn-sm btn-outline-secondary py-0 px-2" title="Copy phone" onClick={() => handleCopy(p.phone)}>⧉</button>
                  </span>
                </div>
                <div className="ms-auto d-flex flex-wrap gap-2"></div>
              </div>
            </div>
          </div>

          <div className="card customer-summary-card border-0 shadow-sm mb-3">
            <div className="card-body customer-summary-body border shadow-sm d-flex flex-column flex-lg-row gap-3">
              {customerSummaryMetrics.map((metric) => (
                <div key={metric.key} className="flex-grow-1 d-flex align-items-center gap-3 customer-summary-metric">
                  <span className={`customer-summary-icon ${metric.accent}`}>
                    <metric.Icon aria-hidden="true" />
                  </span>
                  <div className="flex-grow-1">
                    <div className="text-uppercase text-body-secondary fw-semibold small customer-summary-tracking">
                      {metric.label}
                    </div>
                    <div className="fs-4 fw-semibold mt-1">{metric.value}</div>
                    <small className="text-muted">{metric.caption}</small>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="card border rounded-4 shadow-sm bg-body">
            <div className="card-body">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <div>
                  <div className="text-body-secondary small text-uppercase fw-semibold">View</div>
                  <div className="fw-semibold">{summaryViewLabel}</div>
                </div>
                <div className="ms-auto d-flex align-items-center gap-2">
                  <label htmlFor="customer-view-select" className="text-body-secondary small mb-0">
                    Filter
                  </label>
                  <select
                    id="customer-view-select"
                    className="form-select form-select-sm customer-view-select"
                    value={tab}
                    onChange={(e) => setTab(e.target.value)}
                    aria-label="Filter transfers by view"
                  >
                    {tabOptions.map((option) => (
                      <option key={option.key} value={option.key}>
                        {option.label} ({option.count})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {tab !== "linked" && (
                <>
                  <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                    {Object.entries(statusCounts || {}).map(([s, c]) => (
                      <span key={s} className="badge bg-body-tertiary text-body border">
                        {s}: <span className="fw-semibold">{c}</span>
                      </span>
                    ))}
                    <span className="text-body-secondary small ms-auto">
                      {txCount} {txCount === 1 ? "transfer" : "transfers"}
                    </span>
                  </div>

                  {currentTxs.length ? (
                    <div className="d-flex flex-column">
                      {currentTxs.map((t) => {
                        const asSender = String(t?.senderId ?? "") === String(personId ?? "");
                        const amount = asSender ? (t?.sendAmount ?? t?.amount) : (t?.receiveAmount ?? t?.amount);
                        const currency = asSender ? (t?.sendCurrency ?? t?.currency) : (t?.receiveCurrency ?? t?.currency);
                        const counterpartName = asSender ? (t?.recipientName || t?.recipientPhone || "Recipient")
                          : (t?.senderName || t?.senderPhone || "Sender");
                        return (
                          <button key={t.uuid || t.id} className="text-start border-0 bg-transparent p-0" onClick={() => navigate(`/home/transaction/${t.uuid}`)}>
                            <div className="d-flex align-items-center justify-content-between p-3 rounded-3 border mb-2">
                              <div className="me-3 flex-grow-1">
                                <div className="fw-medium d-flex flex-wrap align-items-center gap-2">
                                  <StatusBadge status={t.status} />
                                  <span className={`badge border ${asSender ? "bg-primary-subtle text-primary-emphasis" : "bg-success-subtle text-success-emphasis"}`} />
                                  <span>{fmtMoney(amount, currency)}</span>
                                  <span className="text-body-secondary small">• {asSender ? "to" : "from"} {counterpartName}</span>
                                  <span className="text-body-secondary small" title={t.createdAt ? new Date(t.createdAt).toLocaleString() : ""}>
                                    • {relativeTimeFromNow(t.createdAt)}
                                  </span>
                                </div>
                              </div>
                              <i className="bi bi-chevron-right" />
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-body-secondary">No transfers in this view.</div>
                  )}
                </>
              )}

              {tab === "linked" && (
                <>
                  <div className="d-flex align-items-center mb-2">
                    <span className="badge bg-body-tertiary text-body border">{linkedEntities.length} unique</span>
                  </div>
                  {linkedEntities.length ? (
                    <div className="d-flex flex-column">
                      {linkedEntities.map((e) => (
                        <button key={e.id} className="text-start border-0 bg-transparent p-0" onClick={() => navigate(`/home/customers/${e.uuid}`)}>
                          <div className="d-flex justify-content-between align-items-center p-3 rounded-3 border mb-2">
                            <div className="d-flex align-items-center gap-3">
                              <div
                                className="rounded-circle d-inline-flex align-items-center justify-content-center bg-body-tertiary"
                                style={{ width: 44, height: 44, fontWeight: 700 }}
                              >
                                {(e.name || "").split(" ").filter(Boolean).slice(0, 2).map((n) => n[0].toUpperCase()).join("") || "•"}
                              </div>
                              <div>
                                <div className="fw-semibold d-flex align-items-center gap-2">
                                  {e.name || "—"}
                                  {e.countryCode ? (
                                    <img src={`https://flagcdn.com/20x15/${e.countryCode}.png`} alt={e.countryName || "Country"} width="20" height="15" className="rounded shadow-sm" />
                                  ) : null}
                                </div>
                                <div className="text-body-secondary small">
                                  {e.phone || "—"}
                                  {e.countryName ? ` · ${e.countryName}` : ""}
                                  {e.lastAt ? ` · ${relativeTimeFromNow(e.lastAt)}` : ""}
                                  {e.count ? ` · ${e.count} ${e.count === 1 ? "transfer" : "transfers"}` : ""}
                                  {" · "}
                                  <span className="badge bg-primary-subtle text-primary-emphasis border me-1">{e.receivedCount > 0 ? "SENDER" : "RECIPIENT"}</span>
                                </div>
                              </div>
                            </div>
                            <i className="bi bi-chevron-right" />
                          </div>
                        </button>
                      ))}
                    </div>
                  ) : (
                    <div className="text-body-secondary">No linked partners yet.</div>
                  )}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
